<template>
  <!-- The one standard bar (navigation-design §1, §7): the server's items in
       its fixed slots. Altar · <their Mystic> · Quest until the Community
       opens, where the Mystic's slot becomes Community; today's bar for every
       account that existed at the deploy. In a reading it collapses to Menu. -->
  <div v-if="bar" class="bottom-menu-bar" :class="{ 'nav-reading-collapsed': bar.collapsed }" data-testid="staged-bar">
    <button v-if="bar.collapsed" class="bottom-menu-collapsed" data-testid="bar-menu" @click="$emit('expand')">
      <i class="fa-solid fa-bars"></i>
      <span>Menu</span>
    </button>
    <template v-else>
      <!-- A reading for someone left paused (decision 2026-11-01): in the opened
           Menu only, while one exists; never in the room or by Get Reading. -->
      <button v-if="bar.close && menuExtra" class="bar-menu-extra" data-testid="bar-menu-paused-gift"
              @click="$emit('extra', menuExtra)">
        <span aria-hidden="true">♡</span> {{ menuExtra.button_label }}
      </button>
      <button v-if="bar.close" class="bottom-menu-item nav-collapse-toggle" data-testid="bar-close" @click="$emit('collapse')">
        <i class="fa-solid fa-chevron-down"></i>
        <span>Close</span>
      </button>
      <!-- The Quest journey (2026-10-27): a reveal's light lands on data-bar-slot;
           a tab waits invisible (pending) while the bar makes room, then is born,
           or a slot changes its face (upgr), or rings (the Circle lands on Mirror). -->
      <button v-for="it in bar.items" :key="it.slot || it.key" :data-testid="`bar-${it.key}`" :data-bar-slot="it.slot || it.key"
              :class="['bottom-menu-item', { active: active === it.key, 'quest-nav-item': it.key === 'quest', shake: it.key === 'studio' && studioShake,
                                             pending: it.pending, born: it.anim === 'born', upgr: it.anim === 'upgr' }]"
              @click="tap(it)">
        <span v-if="it.anim" class="ringb" aria-hidden="true"></span>
        <span v-if="it.key === 'mystic'" class="bar-portrait-wrap">
          <img v-if="it.portrait && !broken" :src="it.portrait" alt="" class="bar-portrait square" @error="broken = true" />
          <span v-else class="bar-initial" aria-hidden="true">{{ (it.label || 'M').charAt(0) }}</span>
          <span v-if="it.dot" class="bar-dot" aria-hidden="true"></span>
        </span>
        <span v-else-if="it.key === 'quest'" class="quest-icon-wrap">
          <i class="fa-solid fa-scroll"></i>
          <!-- Chris 2026-10-28: no number and no dot on Quest. -->
        </span>
        <span v-else-if="it.key === 'mirror'" class="quest-icon-wrap">
          <svg class="bar-mirror-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a6.5 7 0 0 1 6.5 7A6.5 7 0 0 1 13.4 15.8V18h1.1v4h-5v-4h1.1v-2.2A6.5 7 0 0 1 5.5 9 6.5 7 0 0 1 12 2zm0 2.2A4.3 4.8 0 0 0 7.7 9 4.3 4.8 0 0 0 12 13.8 4.3 4.8 0 0 0 16.3 9 4.3 4.8 0 0 0 12 4.2zm-1.8 2.1 1 .6a2.8 2.8 0 0 0-1.4 2.4h-1.2a4 4 0 0 1 1.6-3z" /></svg>
          <span v-if="it.dot" class="bar-dot" aria-hidden="true"></span>
        </span>
        <span v-else class="quest-icon-wrap">
          <i :class="['fa-solid', it.icon]"></i>
          <span v-if="it.dot" class="bar-dot" aria-hidden="true"></span>
        </span>
        <span class="bar-label">{{ it.label }}</span>
        <span v-if="it.dot && it.key !== 'quest'" class="sr-only"> (new)</span>
      </button>
    </template>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { track } from '@/utils/analytics'

const props = defineProps({
  bar: { type: Object, default: null },
  stage: { type: Number, default: 0 },
  active: { type: String, default: '' },
  questBadge: { type: Object, default: null },
  questShake: { type: Boolean, default: false },
  studioShake: { type: Boolean, default: false },
  // The opened Menu's one extra: a paused reading for someone (continue_gift).
  menuExtra: { type: Object, default: null },
})
const emit = defineEmits(['tap', 'expand', 'collapse', 'extra'])
const broken = ref(false)

function tap (it) {
  track('nav_tab', { item: it.key, stage: props.stage })
  emit('tap', it.key, it)
}
</script>

<style scoped>
/* The bar (moved from ReaderPage with the journey bar's removal). */
.bottom-menu-bar {
  position: fixed;
  bottom: env(safe-area-inset-bottom, 5px);
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 1024px;
  height: 65px;
  background-color: #663399;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border-radius: 16px;
  box-shadow: 0 -3px 8px rgba(0, 0, 0, 0.4);
  border-top: 2px solid #663399;
  z-index: 999;
}

.bottom-menu-item {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 6px 18px;
  /* >=44px tap target (the 65px bar has room) — was ~36px, below iOS minimum. */
  min-height: 44px;
  border-radius: 14px;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}
.quest-nav-badge {
  position: absolute; top: -7px; left: 100%; margin-left: 1px;
  min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px;
  background: #e0b64a; color: #1a0a2e; font-size: 10px; font-weight: 800;
  display: flex; align-items: center; justify-content: center; line-height: 1;
  box-shadow: 0 1px 4px rgba(0,0,0,0.5);
}
/* The one place the badge shakes: you completed your minute at the Altar. */
@keyframes quest-badge-shake {
  0%, 100% { transform: rotate(0) scale(1); }
  12% { transform: rotate(-16deg) scale(1.25); }
  28% { transform: rotate(13deg) scale(1.25); }
  44% { transform: rotate(-10deg) scale(1.15); }
  60% { transform: rotate(7deg) scale(1.1); }
  76% { transform: rotate(-4deg) scale(1.05); }
}
.quest-nav-badge.shake { animation: quest-badge-shake 0.7s ease-in-out 2; transform-origin: center; }
.bar-dot.shake { animation: quest-badge-shake 0.7s ease-in-out 2; transform-origin: center; }

.bottom-menu-item i {
  font-size: 20px;
}

.bottom-menu-item:hover { color: #FFDFA3; }
/* Selected tab — deep-purple recessed pill (from the app's dark palette), gold text. */
.bottom-menu-item.active {
  color: #FFDFA3;
  font-weight: 700;
  background: rgba(20, 7, 31, 0.55);
  box-shadow: inset 0 1px 5px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 223, 163, 0.3);
}
.bottom-menu-item.active i { filter: drop-shadow(0 0 6px rgba(255, 223, 163, 0.45)); }

/* ── Reading-session collapsed nav ─────────────────────────────────
   During a reading the bar collapses to a compact "Menu" pill so the reading
   owns the screen; tapping expands the full nav (which leads with a Close
   chevron to re-collapse). */
/* Full width but slim — the point is to give the reading more height, so the
   collapsed bar is a short full-width strip, not the tall 65px nav. */
.bottom-menu-bar.nav-reading-collapsed {
  height: 40px;
  padding: 0;
}
.bottom-menu-collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  height: 100%;
  background: none;
  border: none;
  color: #FFDFA3;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0;
  border-radius: 16px;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}
.bottom-menu-collapsed i { font-size: 15px; }
.bottom-menu-collapsed:hover { background: rgba(20, 7, 31, 0.4); }
.nav-collapse-toggle { color: rgba(255, 255, 255, 0.6); }
/* The paused gift sits just above the opened Menu, its own quiet line. */
.bar-menu-extra {
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  max-width: calc(100% - 24px); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  padding: 9px 18px; border-radius: 999px; border: 1px solid rgba(255, 223, 163, 0.55);
  background: rgba(20, 7, 31, 0.92); color: #FFDFA3; font-size: 15px; cursor: pointer;
}
.bar-menu-extra:hover { background: rgba(60, 25, 90, 0.95); }
.bar-menu-extra:focus-visible { outline: 2px solid #FFDFA3; outline-offset: 2px; }
.nav-collapse-toggle:hover { color: #FFDFA3; }
.bottom-menu-item.shake { animation: quest-badge-shake 0.7s ease-in-out 2; transform-origin: center; }
@media (prefers-reduced-motion: reduce) {
  .bottom-menu-item.shake, .quest-nav-badge.shake, .bar-dot.shake { animation: none; }
}
/* The Mystic's portrait and the dots. */
.bar-portrait-wrap, .quest-icon-wrap { position: relative; display: inline-flex; align-items: center; justify-content: center; }
.bar-portrait { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(240,217,163,0.55); }
/* Squares are Mystics, circles are seekers (quest journey 2026-10-27). */
.bar-portrait.square { width: 22px; height: 22px; border-radius: 5px; border: 0; box-shadow: 0 0 0 1.5px #FFDFA3; }
.bar-mirror-icon { width: 20px; height: 20px; }
.bottom-menu-item { position: relative; }
/* A reveal's arrival (prototype .tab.pending/.born/.upgr/.ringb). */
.bottom-menu-item.pending { opacity: 0; }
.bottom-menu-item.born { animation: bar-born .7s cubic-bezier(.2,.9,.3,1.25) both; }
.bottom-menu-item.upgr > .quest-icon-wrap, .bottom-menu-item.upgr > .bar-portrait-wrap, .bottom-menu-item.upgr > .bar-label { animation: bar-upgr .7s ease-out both; }
@keyframes bar-born { 0% { opacity: 0; transform: scale(.2); } 60% { opacity: 1; transform: scale(1.15); } 100% { transform: scale(1); } }
@keyframes bar-upgr { 0% { opacity: 0; transform: translateY(6px) scale(.7); } 60% { opacity: 1; transform: scale(1.15); } 100% { transform: none; } }
.ringb { position: absolute; left: 50%; top: 16px; width: 16px; height: 16px; margin: -8px 0 0 -8px; border-radius: 50%;
  border: 1.5px solid #ffe7b5; animation: bar-ringb 1s ease-out both; pointer-events: none; }
@keyframes bar-ringb { 0% { transform: scale(.4); opacity: 1; } 100% { transform: scale(4.2); opacity: 0; } }
@keyframes bar-fadein { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .bottom-menu-item.born, .bottom-menu-item.upgr > .quest-icon-wrap, .bottom-menu-item.upgr > .bar-portrait-wrap, .bottom-menu-item.upgr > .bar-label { animation: bar-fadein .6s ease-out both; }
  .ringb { display: none; }
}
.bar-initial { width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Letter Display', 'Cinzel', serif; font-size: 12px; color: #f0d9a3; border: 1px solid rgba(240,217,163,0.55); background: #2a1845; }
.bar-label { max-width: 96px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The portrait menu's dot (TopRightAccount), reused. */
.bar-dot { position: absolute; top: -2px; right: -6px; width: 8px; height: 8px; border-radius: 50%; background: #f0d9a3;
  box-shadow: 0 0 0 2px rgba(102,51,153,0.95); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
